import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Hourglass, XCircle, Pause, Trash } from "@phosphor-icons/react";

// iter_PLATFORM_OWNER_TENANT_MANAGEMENT (2026-02-28) — surfaced when
// the tenant's approval_status is anything other than "approved". The
// backend returns 403 with a `detail.approval_status` payload on every
// protected /api/* call; the app layer routes those users here.

const COPY = {
  pending: {
    Icon: Hourglass,
    title: "Application pending approval",
    body:  "Thanks for signing up. Your account is waiting for Platform Owner approval. You will receive an email as soon as your business is approved.",
    tone:  "amber",
  },
  rejected: {
    Icon: XCircle,
    title: "Application declined",
    body:  "Unfortunately your Margin Mate One application was not approved. If you believe this was in error, contact platform support.",
    tone:  "stone",
  },
  suspended: {
    Icon: Pause,
    title: "Business suspended",
    body:  "Your Margin Mate One business has been suspended by platform administration. All active sessions have been signed out. Contact platform support for details.",
    tone:  "orange",
  },
  deleted: {
    Icon: Trash,
    title: "Business removed",
    body:  "Your Margin Mate One business has been removed. Contact platform support if this was unexpected.",
    tone:  "red",
  },
};

const TONE_CLASSES = {
  amber:  "bg-amber-50  text-amber-800  border-amber-200",
  stone:  "bg-stone-100 text-stone-700  border-stone-200",
  orange: "bg-orange-50 text-orange-800 border-orange-200",
  red:    "bg-red-50    text-red-800    border-red-200",
};

export default function PendingApproval({ status: propStatus }) {
  const { logout, refresh, business } = useAuth();
  const [status, setStatus] = useState(propStatus || "pending");

  useEffect(() => {
    // Try /auth/me one more time — approval may have flipped while
    // this page is open. If already approved, refresh() will bring
    // the user back into the shell.
    refresh().catch(() => {});
    // Extract status from most-recent 403 payload (persisted by api.js).
    try {
      const raw = sessionStorage.getItem("mm_pending_status_payload");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.approval_status) setStatus(parsed.approval_status);
      }
    } catch { /* ignore */ }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const cfg = COPY[status] || COPY.pending;
  const { Icon } = cfg;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--sand-50)] p-6"
         data-testid="pending-approval-page">
      <div className={`max-w-md w-full text-center rounded-2xl border p-8 ${TONE_CLASSES[cfg.tone]}`}
           data-testid={`pending-status-${status}`}>
        <div className="w-20 h-20 rounded-full bg-white/70 flex items-center justify-center mx-auto mb-4">
          <Icon size={40} weight="fill" />
        </div>
        <h1 className="font-heading text-2xl font-semibold mb-2">{cfg.title}</h1>
        <p className="text-sm mb-4">{cfg.body}</p>
        {business?.name && (
          <div className="text-[11px] font-mono opacity-70 mb-4">
            {business.name}
          </div>
        )}
        <button
          onClick={logout}
          data-testid="pending-signout"
          className="h-10 px-6 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] text-sm font-medium">
          Sign out
        </button>
      </div>
    </div>
  );
}
